import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Info, Loader2 } from 'lucide-react';

import { getVocabularyTerm } from '@/api/vocabularies';
import { cn } from '@/lib/utils';

import { useVocabularyInfo } from './useVocabularyInfo';
import { VocabTermInfoContent } from './VocabTermInfo';

interface TermInfoToggleProps {
  readonly open: boolean;
  readonly onToggle: () => void;
  /** Term label — used only for the button's accessible name. */
  readonly termLabel: string;
  readonly className?: string;
}

/** The ⓘ trigger that discloses a {@link TermInfoInline} panel beneath a term row. */
export function TermInfoToggle({
  open,
  onToggle,
  termLabel,
  className,
}: TermInfoToggleProps): ReactElement {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={t('vocab.picker.info', { term: termLabel })}
      aria-expanded={open}
      onClick={onToggle}
      className={cn(
        'shrink-0 inline-flex items-center rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors',
        open && 'text-foreground',
        className,
      )}
    >
      <Info className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

/**
 * Value cap per multi-value field. HPO category terms can list ~100 associated
 * genes; without a cap the inline panel dwarfs the browse list.
 */
const MAX_FIELD_VALUES = 15;

interface TermInfoInlineProps {
  /** Vocabulary the term belongs to (e.g. `hpo`). */
  readonly vocabularyId: string;
  readonly termId: string;
  readonly className?: string;
}

/**
 * In-flow term-detail panel: fetches the full term and renders the shared
 * {@link VocabTermInfoContent} — the same body as the floating phenotype
 * tooltip — inside a bordered block. Used in the HPO browse tree / search
 * rows, where a floating popover would fight the modal's own chrome
 * (PTV2-1217). Pair with {@link TermInfoToggle} + local open state.
 */
export function TermInfoInline({
  vocabularyId,
  termId,
  className,
}: TermInfoInlineProps): ReactElement {
  const { t } = useTranslation();
  const info = useVocabularyInfo(vocabularyId);
  const termQuery = useQuery({
    queryKey: ['vocabulary', vocabularyId, 'term', termId],
    queryFn: ({ signal }) => getVocabularyTerm(vocabularyId, termId, signal),
    staleTime: 5 * 60_000,
  });

  return (
    <div className={cn('rounded-md border border-border bg-popover p-3', className)}>
      {termQuery.isPending ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          {t('patient.phenotypes.browse.loading')}
        </p>
      ) : termQuery.isError ? (
        <p className="text-xs text-destructive">{t('vocab.picker.error')}</p>
      ) : termQuery.data === null ? (
        <p className="text-xs text-muted-foreground">{t('vocab.picker.empty')}</p>
      ) : (
        <VocabTermInfoContent
          term={termQuery.data}
          display={info?.display ?? null}
          vocabularyName={info?.name ?? null}
          version={info?.dataVersion ?? null}
          maxValuesPerField={MAX_FIELD_VALUES}
        />
      )}
    </div>
  );
}
